/**
 * Tila — one line at the top of a module's Työpaja section (#1218,
 * specs/facilitator-console.md → shelf 1): whether its output exists, and each
 * check the module declares (admin.checks), met or not. Facts the platform
 * already has; nothing here knows a module.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { AlertTriangle, Check } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin: 0 0 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
`;

const Item = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${(p) => (p.$tone === 'ok' ? '#a7f3d0' : p.$tone === 'warn' ? '#fcd34d' : 'inherit')};
  svg { flex-shrink: 0; }
`;

function text(labels, language) {
  return labels?.[language] || labels?.fi || '';
}

/**
 * @param {{ instanceId: string, step: object, checks: object[] }} props
 *   `step.hasContent` says whether an output exists; `checks` is the manifest
 *   list, used only to know whether there is anything to fetch.
 */
export default function ModuleStateLine({ instanceId, step, checks = [] }) {
  const { t, language } = useTranslation();
  const [evaluated, setEvaluated] = useState(null);
  const hasOutputs = (step.outputs || []).length > 0;

  useEffect(() => {
    if (!instanceId || !step?.id || !checks.length) return undefined;
    let cancelled = false;
    fetch(API_ENDPOINTS.CHAIN_STEP_CHECKS(instanceId, step.id), { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setEvaluated(d.checks || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [instanceId, step?.id, checks.length]);

  if (!hasOutputs && !checks.length) return null;
  const unmet = (evaluated || []).filter((c) => !c.satisfied);
  return (
    <Line>
      {hasOutputs && (
        <Item $tone={step.hasContent ? 'ok' : undefined}>
          {step.hasContent ? <Check size={14} /> : null}
          {t(step.hasContent ? 'workshopOrchestrator.state.ready' : 'workshopOrchestrator.state.missing')}
        </Item>
      )}
      {evaluated && !unmet.length && (
        <Item $tone="ok"><Check size={14} />{t('workshopOrchestrator.state.checksOk')}</Item>
      )}
      {unmet.map((c) => (
        <Item key={c.id} $tone="warn"><AlertTriangle size={14} />{text(c.labels, language) || c.detail}</Item>
      ))}
    </Line>
  );
}
